/** @prettier */

import {
  FormControlLabel,
  FormGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
} from '@mui/material';
import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {
  deleteCompositionsRTUnit,
  getCompositionsRTUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';

/**
 * 熱源機削除ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const RTDeleteDialog = (props: {
  /** 削除する熱源機ID */
  unitID: string;
  /** 削除する熱源機の名前 */
  unitName: string;
  /** 熱源機の情報（set関数） */
  setRTInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsRTUnit | null>
  >;
  /** ダイアログを開くかどうか */
  isOpenDialog: boolean;
  /** ダイアログを開くかどうか（set関数） */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  //パスパラメータ取得
  const {siteID, gwID} = useParams();

  const [checked1, setChecked1] = useState(false); //1行目のチェックボックス
  const [checked2, setChecked2] = useState(false); //2行目のチェックボックス
  const [okBtnDisabled, setOKBtnDisabled] = useState(true); //OKボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  const trans = useTranslation().t; //翻訳関数

  /**
   * OKボタン押下時処理
   */
  const unitDelete = () => {
    //パスパラメータを取得できない場合
    if (siteID === undefined || gwID === undefined) {
      return;
    }

    setOKBtnDisabled(true);
    setAPIWait(true);

    //熱源機の削除
    deleteCompositionsRTUnit(siteID, gwID, props.unitID)
      .then(() => {
        //熱源機の情報を取得
        return getCompositionsRTUnit(siteID, gwID, {'max-result': 400});
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['unit-informations'].sort(sortByUnicode('unit-name'));

        props.setRTInfo(response);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
        handleClose();
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
    setChecked1(false);
    setChecked2(false);
  };

  /**
   * チェックボックス押下時処理
   * @param event
   * @param check 押下したチェックボックスの名前
   */
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    check: string
  ) => {
    if (check === 'check1') {
      setChecked1(event.target.checked);
      if (event.target.checked === false || checked2 === false) {
        setOKBtnDisabled(true);
      } else {
        setOKBtnDisabled(false);
      }
    }
    if (check === 'check2') {
      setChecked2(event.target.checked);
      if (event.target.checked === false || checked1 === false) {
        setOKBtnDisabled(true);
      } else {
        setOKBtnDisabled(false);
      }
    }
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {trans('RTDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          {/* ユニット名称の表示 */}
          <DialogContentText>
            {trans('RTDeleteDialog.msg')} {props.unitName}
          </DialogContentText>
          {/* チェックボックス */}
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={e => handleChange(e, 'check1')}
                  id={`mv-Check1DeleteRt${props.unitName}`}
                />
              }
              checked={checked1}
              label={trans('RTDeleteDialog.checkboxDescription.unrestorable')}
            />
          </FormGroup>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={e => handleChange(e, 'check2')}
                  id={`mv-Check2DeleteRt${props.unitName}`}
                />
              }
              checked={checked2}
              label={trans(
                'RTDeleteDialog.checkboxDescription.operationDataDeleted'
              )}
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('RTDeleteDialog.buttonTxt.cancel')}
            id={`mv-btnCancelDeleteRt${props.unitName}`}
          />
          <PrimaryButton
            onClick={unitDelete}
            buttonText={trans('RTDeleteDialog.buttonTxt.ok')}
            id={`mv-btnOkDeleteRt${props.unitName}`}
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={`${props.unitName}DeleteErrDlgBtn`}
      />
    </>
  );
};
export default RTDeleteDialog;
